import { useState, useEffect } from 'react';
import { Search, Video, Wrench, FlaskConical, BookOpen, Book, ArrowRight, Clock, Sparkles, Star, CheckCircle, Rocket, Headphones, ChevronDown } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import StatsBar from '@/components/StatsBar';
import BudleeAIChatPanel from '@/components/BudleeAIChatPanel';
import { useSubjectChapter } from '@/context/SubjectChapterContext';
import { usePoints } from '@/context/PointsContext';
import { useNavigate } from 'react-router-dom';
import { subjectsAPI, chaptersAPI } from '@/api/curriculum';
import { curiosityAPI } from '@/curiosity/services/curiosityAPI';
import { getPortalType, getUserData } from '@/utils/auth';
import { getContentThumbnail, getNewsThumbnail } from '@/utils/thumbnailUtils';

export default function CuriosityCentre() {
  const { points } = usePoints();
  const navigate = useNavigate();
  const {
    selectedSubject,
    selectedChapter,
    selectedSubjectId,
    selectedChapterId,
    updateSubject,
    updateChapter,
  } = useSubjectChapter();
  const [showCore, setShowCore] = useState(true); // Show CORE checkbox
  const [showGoDeeper, setShowGoDeeper] = useState(true); // Show GO DEEPER checkbox
  const [activeTab, setActiveTab] = useState('all'); // Content type filter
  const [searchQuery, setSearchQuery] = useState('');
  const [isSelectionExpanded, setIsSelectionExpanded] = useState(false); // Mobile selection panel state

  // Backend data
  const [subjects, setSubjects] = useState([]);
  const [chapters, setChapters] = useState([]);
  const [contentItems, setContentItems] = useState([]);
  const [exploreTopics, setExploreTopics] = useState([]);
  const [relatedNews, setRelatedNews] = useState([]);
  const [loadingSubjects, setLoadingSubjects] = useState(true);
  const [loadingContent, setLoadingContent] = useState(false);
  const [loadingTopics, setLoadingTopics] = useState(false);
  const [loadingNews, setLoadingNews] = useState(false);
  const [showChatPanel, setShowChatPanel] = useState(false);
  const [prefilledText, setPrefilledText] = useState('');

  const isSmallScreen = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(max-width: 767px)').matches;

  // Fetch subjects and chapters from backend
  useEffect(() => {
    const loadSubjectsAndChapters = async () => {
      try {
        const userData = getUserData(getPortalType(window.location.pathname || ''));
        if (!userData || !userData.board || !userData.grade) {
          setLoadingSubjects(false);
          return;
        }

        const boardId = userData.board.id;
        const gradeId = userData.grade.id;

        // Fetch subjects
        const subjectsList = await subjectsAPI.list({ board: boardId, grade: gradeId });
        const subjectsFormatted = subjectsList.map(s => ({
          value: s.id.toString(),
          label: s.subject_name,
          id: s.id
        }));
        setSubjects(subjectsFormatted);
        setLoadingSubjects(false);
      } catch (error) {
        console.error('Error loading subjects:', error);
        setLoadingSubjects(false);
      }
    };

    loadSubjectsAndChapters();
  }, []);

  // Fetch chapters when subject is selected
  useEffect(() => {
    const loadChapters = async () => {
      if (!selectedSubjectId) {
        setChapters([]);
        setContentItems([]);
        setExploreTopics([]);
        setRelatedNews([]);
        return;
      }

      try {
        const chaptersList = await chaptersAPI.list({ subject: selectedSubjectId });
        const chaptersFormatted = chaptersList.map(c => ({
          value: c.id.toString(),
          label: c.chapter_name,
          id: c.id
        }));
        setChapters(chaptersFormatted);
      } catch (error) {
        console.error('Error loading chapters:', error);
        setChapters([]);
      }
    };

    loadChapters();
  }, [selectedSubjectId]);

  // Fetch content, topics, and news when chapter/subject selection changes
  useEffect(() => {
    const loadChapterContent = async () => {
      // Load content if chapter OR subject is selected (or if nothing is selected - shows all for board/grade)
      setLoadingContent(true);
      setLoadingTopics(true);
      setLoadingNews(true);

      try {
        // Fetch chapter content (includes core_content, deep_content, explore_topics, related_news)
        // Pass 0 as chapter_id if not selected to allow flexible content loading
        console.log('Fetching chapter content - chapter ID:', selectedChapterId, 'subject ID:', selectedSubjectId);
        const chapterData = await curiosityAPI.getChapterContent(
          selectedChapterId || 0,
          selectedSubjectId || null
        );
        console.log('Chapter data received:', chapterData);
        console.log('Core content:', chapterData.core_content);
        console.log('Deep content:', chapterData.deep_content);

        // Transform backend data to match UI format
        const transformedItems = [];

        // Helper function to convert plural content type to singular
        const pluralToSingular = (plural) => {
          const map = {
            'videos': 'video',
            'podcasts': 'podcast',
            'simulations': 'simulation',
            'tools': 'tool',
            'courses': 'course',
            'books': 'book'
          };
          return map[plural] || plural.slice(0, -1); // Default: remove 's'
        };

        // Helper function to format duration
        const formatDuration = (item) => {
          if (item.video_duration_formatted) {
            return item.video_duration_formatted;
          } else if (item.audio_duration_formatted) {
            return item.audio_duration_formatted;
          } else if (item.course_duration_hours) {
            return `${item.course_duration_hours} hrs`;
          } else if (item.read_time_minutes) {
            return `${item.read_time_minutes} min`;
          }
          return 'N/A';
        };

        // Process core content
        if (chapterData.core_content) {
          Object.keys(chapterData.core_content).forEach(contentType => {
            const items = chapterData.core_content[contentType] || [];
            items.forEach(item => {
              const singularType = pluralToSingular(contentType);
              transformedItems.push({
                id: item.id,
                type: singularType,
                content_type: item.content_type || singularType,
                category: 'core',
                title: item.title,
                description: item.description || '',
                thumbnail: getContentThumbnail(item, 400, 300),
                duration: formatDuration(item),
                tag_type: item.tag_type,
                isWatched: item.is_watched || false, // Use is_watched from API response
                ...item
              });
            });
          });
        }

        // Process deep content
        if (chapterData.deep_content) {
          Object.keys(chapterData.deep_content).forEach(contentType => {
            const items = chapterData.deep_content[contentType] || [];
            items.forEach(item => {
              const singularType = pluralToSingular(contentType);
              transformedItems.push({
                id: item.id,
                type: singularType,
                content_type: item.content_type || singularType,
                category: 'go-deeper',
                title: item.title,
                description: item.description || '',
                thumbnail: getContentThumbnail(item, 400, 300),
                duration: formatDuration(item),
                tag_type: item.tag_type,
                isWatched: item.is_watched || false, // Use is_watched from API response
                ...item
              });
            });
          });
        }

        console.log('Transformed items:', transformedItems);
        console.log('Total items:', transformedItems.length);
        setContentItems(transformedItems);
        setLoadingContent(false);

        // Set explore topics
        if (chapterData.explore_topics && Array.isArray(chapterData.explore_topics)) {
          const transformedTopics = chapterData.explore_topics.map((topic, index) => ({
            id: topic.id,
            title: topic.title,
            description: topic.description || '',
            icon: '🔬', // Default icon
            color: 'from-purple-500 to-pink-600',
            contentCount: topic.resource_count || 0
          }));
          setExploreTopics(transformedTopics);
        } else {
          setExploreTopics([]);
        }
        setLoadingTopics(false);

        // Set related news
        if (chapterData.related_news && Array.isArray(chapterData.related_news)) {
          const transformedNews = chapterData.related_news.map(news => ({
            id: news.id,
            title: news.title,
            summary: news.summary || news.description || '',
            image: getNewsThumbnail(news, 200, 200),
            category: news.category || 'General',
            source: news.source || 'Curiosity Centre',
            readTime: '2 min'
          }));
          setRelatedNews(transformedNews);
        } else {
          setRelatedNews([]);
        }
        setLoadingNews(false);

      } catch (error) {
        console.error('Error loading chapter content:', error);
        console.error('Error details:', error.response?.data || error.message);
        console.error('Error status:', error.response?.status);
        console.error('Chapter ID that failed:', selectedChapterId);
        setContentItems([]);
        setExploreTopics([]);
        setRelatedNews([]);
        setLoadingContent(false);
        setLoadingTopics(false);
        setLoadingNews(false);
      }
    };

    loadChapterContent();
  }, [selectedChapterId]);

  // Filter content based on checkboxes, content type, and search
  const filteredContent = contentItems.filter(item => {
    // Category filter based on checkboxes
    const matchesCategory = (showCore && item.category === 'core') ||
      (showGoDeeper && item.category === 'go-deeper');

    const matchesTab = activeTab === 'all' || item.content_type === activeTab || item.type === activeTab;
    const matchesSearch = item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (item.description && item.description.toLowerCase().includes(searchQuery.toLowerCase()));
    return matchesCategory && matchesTab && matchesSearch;
  });

  // Content type config
  const contentTypeConfig = {
    video: { icon: Video, color: 'from-red-500 to-rose-600', buttonText: 'Watch Now' },
    podcast: { icon: Headphones, color: 'from-indigo-500 to-purple-600', buttonText: 'Listen Now' },
    tool: { icon: Wrench, color: 'from-blue-500 to-cyan-600', buttonText: 'Try Tool' },
    simulation: { icon: FlaskConical, color: 'from-purple-500 to-pink-600', buttonText: 'Open Simulation' },
    course: { icon: BookOpen, color: 'from-green-500 to-emerald-600', buttonText: 'Start Course' },
    book: { icon: Book, color: 'from-orange-500 to-amber-600', buttonText: 'Read Now' }
  };

  // Content type tabs
  const tabs = [
    { id: 'all', label: 'All', icon: Sparkles },
    { id: 'video', label: 'Videos', icon: Video },
    { id: 'podcast', label: 'Podcasts', icon: Headphones },
    { id: 'tool', label: 'Tools', icon: Wrench },
    { id: 'simulation', label: 'Simulations', icon: FlaskConical },
    { id: 'course', label: 'Courses', icon: BookOpen },
    { id: 'book', label: 'Books', icon: Book }
  ];


  const handleContentClick = (item) => {
    // Navigate based on content type
    if (item.content_type === 'podcast') {
      navigate(`/student/curiosity/podcast/${item.id}`, { state: { item } });
    } else if (item.content_type === 'video') {
      navigate(`/student/curiosity/video/${item.id}`, { state: { item } });
    } else {
      navigate(`/student/curiosity/content/${item.id}`, { state: { item } });
    }
  };

  const handleNewsClick = (news) => {
    navigate(`/student/curiosity/news/${news.id}`, { state: { news } });
  };

  const handleTopicClick = (topic) => {
    navigate(`/student/curiosity/topics/${topic.id}`, { state: { topic } });
  };

  const handleViewAllTopics = () => {
    navigate('/student/curiosity/topics');
  };

  const handleAskBudleeAI = () => {
    // Build context from selected chapter/subject
    let contextText = '';
    if (selectedChapterId && selectedChapter) {
      contextText = `Chapter: ${selectedChapter}`;
      if (selectedSubjectId && selectedSubject) {
        contextText += `, Subject: ${selectedSubject}`;
      }
    } else if (selectedSubjectId && selectedSubject) {
      contextText = `Subject: ${selectedSubject}`;
    } else {
      contextText = 'General Curiosity Centre';
    }

    setPrefilledText(contextText);
    setShowChatPanel(true);
  };

  return (
    <div className="flex flex-col h-full bg-[hsl(var(--main-bg))] overflow-y-auto">
      {/* Mobile StatsBar - Show at top on mobile only */}
      <div className="flex-shrink-0 pt-2 pb-2 flex justify-center lg:hidden">
        <div className="w-fit">
          <StatsBar />
        </div>
      </div>

      {/* Mobile: Subject & Chapter Toggle Button - Show on mobile only, positioned top right */}
      <Button
        onClick={() => setIsSelectionExpanded(!isSelectionExpanded)}
        variant="outline"
        size="icon"
        className="flex lg:hidden fixed right-3 sm:right-4 top-3 sm:top-4 rounded-full bg-gradient-to-br from-[hsl(var(--green-bright))] to-[hsl(var(--teal-vivid))] hover:from-[hsl(var(--green-bright))]/90 hover:to-[hsl(var(--teal-vivid))]/90 border-0 text-white h-10 sm:h-12 w-10 sm:w-12 z-40"
        title="Select Subject & Chapter"
      >
        <ChevronDown className={`w-5 h-5 sm:w-6 sm:h-6 transition-transform ${isSelectionExpanded ? 'rotate-180' : ''}`} />
      </Button>

      {/* Mobile: Selection Modal Floater - Show when expanded */}
      {isSelectionExpanded && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4 lg:hidden">
          <div className="mx-auto w-full px-4 sm:px-6 max-w-2xl sm:max-w-3xl">
            <Card className="bg-gradient-to-br from-[hsl(var(--green-bright))] to-[hsl(var(--teal-vivid))] border-0 shadow-2xl p-4 sm:p-6">
              <div className="space-y-3 sm:space-y-4">
                <h2 className="text-lg sm:text-xl font-bold text-[hsl(var(--main-bg))]">
                  Select Subject & Chapter
                </h2>
                <div className="flex flex-col gap-3 sm:gap-4">
                  <div className="flex-1 w-full">
                    <Select
                      value={selectedSubjectId ? selectedSubjectId.toString() : ''}
                      onValueChange={(value) => {
                        const subject = subjects.find(s => s.id.toString() === value);
                        updateSubject(subject?.id || null, subject?.label || '');
                      }}
                      disabled={loadingSubjects}
                    >
                      <SelectTrigger className="w-full h-10 sm:h-12 bg-[hsl(var(--main-bg))]/90 border-[hsl(var(--main-bg))]/30 text-white hover:bg-[hsl(var(--main-bg))] font-semibold text-sm sm:text-base focus:ring-1 focus:ring-[hsl(var(--green-bright))] [&>svg]:text-white [&>svg]:opacity-100">
                        <SelectValue placeholder={loadingSubjects ? "Loading subjects..." : "Subject"} />
                      </SelectTrigger>
                      <SelectContent className="bg-[hsl(var(--card-bg))] border-[hsl(var(--card-border))]">
                        {subjects.map(subject => (
                          <SelectItem
                            key={subject.id}
                            value={subject.id.toString()}
                            className="text-gray-200 hover:bg-[hsl(var(--sidebar-hover))] hover:text-white cursor-pointer font-medium focus:bg-[hsl(var(--sidebar-hover))] focus:text-white"
                          >
                            {subject.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex-1 w-full">
                    <Select
                      value={selectedChapterId ? selectedChapterId.toString() : ''}
                      onValueChange={(value) => {
                        const chapter = chapters.find(c => c.id.toString() === value);
                        updateChapter(chapter?.id || null, chapter?.label || '');
                      }}
                      disabled={!selectedSubjectId || loadingSubjects}
                    >
                      <SelectTrigger className="w-full h-10 sm:h-12 bg-[hsl(var(--main-bg))]/90 border-[hsl(var(--main-bg))]/30 text-white hover:bg-[hsl(var(--main-bg))] font-semibold text-sm sm:text-base focus:ring-1 focus:ring-[hsl(var(--green-bright))] [&>svg]:text-white [&>svg]:opacity-100 disabled:opacity-50">
                        <SelectValue placeholder="Chapter" />
                      </SelectTrigger>
                      <SelectContent className="bg-[hsl(var(--card-bg))] border-[hsl(var(--card-border))]">
                        {chapters.map(chapter => (
                          <SelectItem
                            key={chapter.id}
                            value={chapter.id.toString()}
                            className="text-gray-200 hover:bg-[hsl(var(--sidebar-hover))] hover:text-white cursor-pointer font-medium focus:bg-[hsl(var(--sidebar-hover))] focus:text-white"
                          >
                            {chapter.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <Button
                  onClick={() => setIsSelectionExpanded(false)}
                  className="w-full bg-[hsl(var(--main-bg))] hover:bg-[hsl(var(--main-bg))]/90 text-white font-semibold mt-2"
                >
                  Done
                </Button>
              </div>
            </Card>
          </div>
        </div>
      )}

      {/* Header: Subject/Chapter Dropdowns - Fixed Height (matching QuestsPage) */}
      <div className="flex-none h-[76px] bg-[hsl(var(--main-bg))] flex items-center z-40 hidden lg:flex">
        {/* Subject & Chapter Dropdowns - Dynamically shrink when chat is open */}
        <div className={`flex items-center min-w-0 transition-all duration-300 ${!isSmallScreen && showChatPanel ? 'flex-1 px-4 pr-[416px]' : 'flex-1 justify-center px-4 sm:px-6'}`}>
          <div className={`bg-gradient-to-br from-[hsl(var(--green-bright))] to-[hsl(var(--teal-vivid))] rounded-lg py-3 px-4 flex items-center gap-2 sm:gap-3 md:gap-4 shadow-2xl border-0 transition-all duration-300 ${showChatPanel ? 'w-full' : 'w-full max-w-5xl mx-6'}`}>
            <div className="flex-1 min-w-[150px]">
              <Select
                value={selectedSubjectId ? selectedSubjectId.toString() : ''}
                onValueChange={(value) => {
                  const subject = subjects.find(s => s.id.toString() === value);
                  updateSubject(subject?.id || null, subject?.label || '');
                }}
                disabled={loadingSubjects}
              >
                <SelectTrigger className="w-full h-8 sm:h-10 bg-[hsl(var(--main-bg))]/90 border-[hsl(var(--main-bg))]/30 text-white hover:bg-[hsl(var(--main-bg))] font-semibold text-xs sm:text-sm md:text-base focus:ring-1 focus:ring-[hsl(var(--green-bright))] [&>svg]:text-white [&>svg]:opacity-100">
                  <SelectValue placeholder={loadingSubjects ? "Loading subjects..." : "Subject"} />
                </SelectTrigger>
                <SelectContent className="bg-[hsl(var(--card-bg))] border-[hsl(var(--card-border))]">
                  {subjects.map(subject => (
                    <SelectItem
                      key={subject.id}
                      value={subject.id.toString()}
                      className="text-gray-200 hover:bg-[hsl(var(--sidebar-hover))] hover:text-white cursor-pointer font-medium focus:bg-[hsl(var(--sidebar-hover))] focus:text-white"
                    >
                      {subject.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex-1 min-w-[150px]">
              <Select
                value={selectedChapterId ? selectedChapterId.toString() : ''}
                onValueChange={(value) => {
                  const chapter = chapters.find(c => c.id.toString() === value);
                  updateChapter(chapter?.id || null, chapter?.label || '');
                }}
                disabled={!selectedSubjectId || loadingSubjects}
              >
                <SelectTrigger className="w-full h-8 sm:h-10 bg-[hsl(var(--main-bg))]/90 border-[hsl(var(--main-bg))]/30 text-white hover:bg-[hsl(var(--main-bg))] font-semibold text-xs sm:text-sm md:text-base focus:ring-1 focus:ring-[hsl(var(--green-bright))] [&>svg]:text-white [&>svg]:opacity-100">
                  <SelectValue placeholder="Chapter" />
                </SelectTrigger>
                <SelectContent className="bg-[hsl(var(--card-bg))] border-[hsl(var(--card-border))]">
                  {chapters.map(chapter => (
                    <SelectItem
                      key={chapter.id}
                      value={chapter.id.toString()}
                      className="text-gray-200 hover:bg-[hsl(var(--sidebar-hover))] hover:text-white cursor-pointer font-medium focus:bg-[hsl(var(--sidebar-hover))] focus:text-white"
                    >
                      {chapter.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Right: Stats Bar */}
        {!showChatPanel && (
          <div className="flex items-center flex-shrink-0 ml-4">
            <div className="[&_[data-stats-bar]]:bg-transparent [&_[data-stats-bar]]:border-0 [&_[data-stats-bar]]:shadow-none">
              <StatsBar />
            </div>
          </div>
        )}
      </div>

      {/* Search and Tabs Section */}
      <div className={`px-6 py-4 space-y-4 transition-all duration-300 ${!isSmallScreen && showChatPanel ? 'pr-[416px]' : ''}`}>
        {/* Search Bar */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-muted-foreground" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search for videos, tools, simulations..."
            className="pl-8 sm:pl-10 bg-[hsl(var(--card-bg))] border-[hsl(var(--card-border))] text-foreground h-10 sm:h-12 text-sm sm:text-base"
          />
        </div>

        {/* Category Checkboxes */}
        <div className="flex flex-row items-center justify-center sm:justify-start gap-3 sm:gap-6">
          <label className="flex items-center gap-2 cursor-pointer group">
            <input
              type="checkbox"
              checked={showCore}
              onChange={(e) => setShowCore(e.target.checked)}
              className="w-4 h-4 sm:w-5 sm:h-5 rounded border-2 border-[hsl(var(--card-border))] bg-[hsl(var(--card-bg))] checked:bg-[hsl(var(--orange-warm))] checked:border-[hsl(var(--orange-warm))] cursor-pointer transition-all"
            />
            <span className="text-xs sm:text-sm font-semibold text-foreground group-hover:text-[hsl(var(--orange-warm))] transition-colors flex items-center gap-1 sm:gap-2">
              <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4" />
              CORE
            </span>
          </label>

          <label className="flex items-center gap-2 cursor-pointer group">
            <input
              type="checkbox"
              checked={showGoDeeper}
              onChange={(e) => setShowGoDeeper(e.target.checked)}
              className="w-4 h-4 sm:w-5 sm:h-5 rounded border-2 border-[hsl(var(--card-border))] bg-[hsl(var(--card-bg))] checked:bg-gradient-to-r checked:from-purple-500 checked:to-pink-600 checked:border-transparent cursor-pointer transition-all"
            />
            <span className="text-xs sm:text-sm font-semibold text-foreground group-hover:text-purple-400 transition-colors flex items-center gap-1 sm:gap-2">
              <Rocket className="w-3 h-3 sm:w-4 sm:h-4" />
              GO DEEPER
            </span>
          </label>
        </div>

        {/* Content Type Filter Tabs */}
        <div className="flex items-center justify-center gap-1 sm:gap-2 overflow-x-auto pb-2">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <Button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                variant={activeTab === tab.id ? 'default' : 'outline'}
                className={`${activeTab === tab.id
                  ? 'bg-[hsl(var(--primary))] text-white border-[hsl(var(--primary))]'
                  : 'bg-[hsl(var(--card-bg))] border-[hsl(var(--card-border))] text-gray-300 hover:bg-[hsl(var(--sidebar-hover))] hover:text-white'
                  } flex items-center justify-center gap-1 sm:gap-2 whitespace-nowrap text-xs sm:text-sm px-2 sm:px-4 py-1.5 sm:py-2 h-auto min-w-[44px] sm:min-w-0`}
                title={tab.label} // Tooltip for mobile icon-only buttons
              >
                <Icon className="w-4 h-4 sm:w-3 sm:h-3" />
                <span className="hidden sm:inline">{tab.label}</span>
              </Button>
            );
          })}
        </div>
      </div>

      {/* Main Content Area */}
      <div className={`flex-1 px-3 sm:px-6 pb-6 ${isSmallScreen && showChatPanel ? 'h-1/2 overflow-auto' : ''}`}>
        <div className={`flex gap-6 transition-all duration-300 ${!isSmallScreen && showChatPanel ? 'pr-[416px]' : ''}`}>
          {/* Left/Center Content - Card Grid + Sidebar sections on mobile */}
          <div className="flex-1 space-y-8">
            {/* Content Cards Grid */}
            {loadingContent ? (
              <div className="flex items-center justify-center py-12">
                <div className="text-muted-foreground">Loading content...</div>
              </div>
            ) : filteredContent.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 px-4 text-center bg-gradient-to-br from-[hsl(var(--card-bg))] to-[hsl(var(--card-bg))] border border-[hsl(var(--card-border))] rounded-2xl shadow-sm relative overflow-hidden group">
                <div className="absolute inset-0 bg-gradient-to-r from-purple-500/10 via-pink-500/10 to-orange-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>

                <div className="relative z-10 p-4 bg-[hsl(var(--card-bg))] rounded-full border border-[hsl(var(--card-border))] mb-6 shadow-lg shadow-purple-500/20">
                  {selectedChapterId ? (
                    <Sparkles className="w-10 h-10 text-purple-400 animate-pulse" />
                  ) : (
                    <Rocket className="w-10 h-10 text-blue-400 animate-bounce" />
                  )}
                </div>

                <h3 className="text-xl sm:text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-purple-400 to-pink-600 mb-3 relative z-10">
                  {selectedChapterId ? "Awesome things are brewing!" : "Ignite your curiosity!"}
                </h3>

                <p className="text-sm sm:text-base text-muted-foreground max-w-md mx-auto leading-relaxed relative z-10">
                  {selectedChapterId
                    ? "We're brewing up some incredible new content for this chapter. Check back soon for mind-blowing videos, interactive simulations, and more!"
                    : "Select a chapter to dive into topics you've covered and discover fascinating content that goes far beyond the textbook."}
                </p>

                {selectedChapterId && (
                  <div className="mt-8 relative z-10 flex items-center justify-center text-xs font-medium text-purple-300 bg-purple-500/10 px-4 py-2 rounded-full border border-purple-500/20">
                    <Rocket className="w-4 h-4 mr-2 animate-bounce" />
                    Stay curious, stay awesome.
                  </div>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 lg:gap-6">
                {filteredContent.map((item) => {
                  // Use content_type if available, otherwise fallback to type
                  const itemType = item.content_type || item.type;
                  const config = contentTypeConfig[itemType] || contentTypeConfig[item.type] || contentTypeConfig.video;
                  const Icon = config.icon;

                  return (
                    <Card
                      key={item.id}
                      className="bg-[hsl(var(--card-bg))] border-[hsl(var(--card-border))] overflow-hidden hover:border-[hsl(var(--primary))] transition-all duration-300 hover:shadow-xl cursor-pointer group"
                      onClick={() => handleContentClick(item)}
                    >
                      {/* Thumbnail */}
                      <div className="relative h-40 sm:h-48 overflow-hidden">
                        <img
                          src={item.thumbnail}
                          alt={item.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                        {/* Category Badge */}
                        <Badge
                          className={`absolute top-2 sm:top-3 left-2 sm:left-3 ${item.category === 'core'
                            ? 'bg-[hsl(var(--orange-warm))] text-white'
                            : 'bg-gradient-to-r from-purple-500 to-pink-600 text-white'
                            } font-bold uppercase text-[10px] sm:text-xs px-2 sm:px-3 py-0.5 sm:py-1`}
                        >
                          {item.category === 'core' ? 'CORE' : 'GO DEEPER'}
                        </Badge>
                        {/* Type Badge */}
                        <Badge
                          className={`absolute top-2 sm:top-3 right-2 sm:right-3 bg-gradient-to-r ${config.color} text-white font-semibold text-[9px] sm:text-xs px-1.5 sm:px-2 py-0.5 sm:py-1 flex items-center gap-1`}
                        >
                          <Icon className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
                          {(item.content_type || item.type || 'content').charAt(0).toUpperCase() + (item.content_type || item.type || 'content').slice(1)}
                        </Badge>
                        {/* Watched Badge - Only for video and podcast */}
                        {(item.content_type === 'video' || item.content_type === 'podcast') && item.isWatched && (
                          <Badge className="absolute top-10 sm:top-12 right-2 sm:right-3 bg-green-500/90 text-white font-semibold text-[9px] sm:text-xs px-1.5 sm:px-2 py-0.5 sm:py-1 flex items-center gap-1">
                            <CheckCircle className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
                            Watched
                          </Badge>
                        )}
                        {/* Duration */}
                        <div className="absolute bottom-2 sm:bottom-3 right-2 sm:right-3 bg-black/70 text-white text-[9px] sm:text-xs px-1.5 sm:px-2 py-0.5 sm:py-1 rounded flex items-center gap-1">
                          <Clock className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
                          {item.duration}
                        </div>
                      </div>

                      {/* Content */}
                      <CardContent className="p-2.5 sm:p-3 lg:p-4 space-y-1.5 sm:space-y-2 lg:space-y-3">
                        <h3 className="text-sm sm:text-base lg:text-lg font-bold text-foreground group-hover:text-[hsl(var(--primary))] transition-colors line-clamp-2">
                          {item.title}
                        </h3>
                        <p className="text-xs sm:text-sm text-muted-foreground line-clamp-2">
                          {item.description}
                        </p>
                        <Button
                          className={`w-full bg-gradient-to-r ${config.color} hover:opacity-90 text-white font-semibold text-xs sm:text-sm py-1 sm:py-1.5 lg:py-2`}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleContentClick(item);
                          }}
                        >
                          {config.buttonText}
                        </Button>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}

            {/* Explore More Topics & Related News - Shown below content on mobile, hidden on desktop */}
            <div className="lg:hidden space-y-4 sm:space-y-6">
              {/* Explore More Topics */}
              <Card className="bg-[hsl(var(--card-bg))] border-[hsl(var(--card-border))]">
                <CardContent className="p-3 sm:p-4 lg:p-6 space-y-2 sm:space-y-3 lg:space-y-4">
                  <h3 className="text-base sm:text-lg lg:text-xl font-bold text-foreground flex items-center gap-1.5 sm:gap-2">
                    <Sparkles className="w-3.5 h-3.5 sm:w-4 sm:h-4 lg:w-5 lg:h-5 text-[hsl(var(--primary))]" />
                    Explore More Topics
                  </h3>

                  <div className="space-y-2 sm:space-y-3">
                    {loadingTopics ? (
                      <div className="text-xs sm:text-sm text-muted-foreground py-3 sm:py-4 text-center">Loading topics...</div>
                    ) : exploreTopics.length === 0 ? (
                      <div className="flex flex-col items-center justify-center p-4 text-center rounded-xl bg-[hsl(var(--main-bg))] border border-[hsl(var(--card-border))] border-dashed my-2 group">
                        <Sparkles className="w-5 h-5 text-purple-400 mb-2 opacity-50 group-hover:opacity-100 group-hover:animate-pulse transition-opacity" />
                        <p className="text-xs sm:text-sm text-muted-foreground font-medium">New topics arriving soon!</p>
                        <p className="text-[10px] sm:text-xs text-muted-foreground/70 mt-1">Stay tuned for more mind-expanding content.</p>
                      </div>
                    ) : (
                      exploreTopics.slice(0, 4).map((topic) => (
                        <Card
                          key={topic.id}
                          className="bg-[hsl(var(--main-bg))] border-[hsl(var(--card-border))] overflow-hidden hover:border-[hsl(var(--primary))] transition-all cursor-pointer group"
                          onClick={() => handleTopicClick(topic)}
                        >
                          <CardContent className="p-2 sm:p-3 lg:p-4">
                            <div className="flex items-center gap-1.5 sm:gap-2 lg:gap-3">
                              <div className={`w-8 h-8 sm:w-10 sm:h-10 lg:w-12 lg:h-12 rounded-lg bg-gradient-to-br ${topic.color} flex items-center justify-center text-sm sm:text-lg lg:text-2xl flex-shrink-0`}>
                                {topic.icon}
                              </div>
                              <div className="flex-1 min-w-0">
                                <h4 className="font-bold text-[10px] sm:text-xs lg:text-sm text-foreground group-hover:text-[hsl(var(--primary))] transition-colors line-clamp-1">
                                  {topic.title}
                                </h4>
                                <p className="text-[8px] sm:text-[10px] lg:text-xs text-muted-foreground">{topic.contentCount} resources</p>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))
                    )}
                  </div>

                  <Button
                    className="w-full bg-[hsl(var(--primary))] hover:bg-[hsl(var(--teal-glow))] text-white font-semibold text-xs sm:text-sm py-1.5 sm:py-2"
                    onClick={handleViewAllTopics}
                  >
                    View All Topics
                    <ArrowRight className="w-3 h-3 sm:w-4 sm:h-4 ml-1.5 sm:ml-2" />
                  </Button>
                </CardContent>
              </Card>

              {/* Related News - Compact Design */}
              <Card className="bg-[hsl(var(--card-bg))] border-[hsl(var(--card-border))]">
                <CardContent className="p-3 sm:p-4 lg:p-6 space-y-2 sm:space-y-3 lg:space-y-4">
                  <h3 className="text-base sm:text-lg lg:text-xl font-bold text-foreground flex items-center gap-1.5 sm:gap-2">
                    <Sparkles className="w-3.5 h-3.5 sm:w-4 sm:h-4 lg:w-5 lg:h-5 text-[hsl(var(--primary))]" />
                    Related News
                  </h3>

                  <div className="space-y-2 sm:space-y-2.5 lg:space-y-3">
                    {loadingNews ? (
                      <div className="text-xs sm:text-sm text-muted-foreground py-3 sm:py-4 text-center">Loading news...</div>
                    ) : relatedNews.length === 0 ? (
                      <div className="flex flex-col items-center justify-center p-4 text-center rounded-xl bg-[hsl(var(--main-bg))] border border-[hsl(var(--card-border))] border-dashed my-2 group">
                        <Sparkles className="w-5 h-5 text-blue-400 mb-2 opacity-50 group-hover:opacity-100 group-hover:animate-pulse transition-opacity" />
                        <p className="text-xs sm:text-sm text-muted-foreground font-medium">Extra! Extra! Coming Soon...</p>
                        <p className="text-[10px] sm:text-xs text-muted-foreground/70 mt-1">We're sourcing the latest news for you.</p>
                      </div>
                    ) : (
                      relatedNews.map((news) => (
                        <Card
                          key={news.id}
                          className="bg-[hsl(var(--main-bg))] border-[hsl(var(--card-border))] overflow-hidden hover:border-[hsl(var(--primary))] transition-all cursor-pointer group"
                          onClick={() => handleNewsClick(news)}
                        >
                          <CardContent className="p-1.5 sm:p-2 lg:p-3">
                            {/* Horizontal compact layout */}
                            <div className="flex gap-1.5 sm:gap-2 lg:gap-3">
                              {/* Small thumbnail on left */}
                              <div className="relative w-12 h-12 sm:w-14 sm:h-14 lg:w-20 lg:h-20 flex-shrink-0 rounded-lg overflow-hidden">
                                <img
                                  src={news.image}
                                  alt={news.title}
                                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                />
                                <Badge className="absolute top-0.5 left-0.5 bg-[hsl(var(--primary))] text-white text-[6px] sm:text-[8px] lg:text-[10px] px-0.5 sm:px-1 lg:px-1.5 py-0.5">
                                  {news.category}
                                </Badge>
                              </div>

                              {/* Content on right */}
                              <div className="flex-1 min-w-0 space-y-0.5">
                                <h4 className="font-bold text-[8px] sm:text-[9px] lg:text-xs text-foreground group-hover:text-[hsl(var(--primary))] transition-colors line-clamp-2 leading-tight">
                                  {news.title}
                                </h4>
                                <p className="text-[7px] sm:text-[8px] lg:text-[10px] text-muted-foreground line-clamp-2 leading-tight">
                                  {news.summary}
                                </p>
                                <div className="flex items-center justify-between text-[6px] sm:text-[7px] lg:text-[9px] text-muted-foreground">
                                  <span className="truncate">{news.source}</span>
                                  <span className="flex-shrink-0 ml-1">{news.readTime}</span>
                                </div>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))
                    )}
                  </div>

                  <Button
                    className="w-full bg-[hsl(var(--primary))] hover:bg-[hsl(var(--teal-glow))] text-white font-semibold text-xs sm:text-sm py-1.5 sm:py-2"
                    onClick={() => navigate('/student/curiosity/news')}
                  >
                    View All News
                    <ArrowRight className="w-3 h-3 sm:w-4 sm:h-4 ml-1.5 sm:ml-2" />
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Right Sidebar - Explore More Topics + Related News (shown on desktop only when chat panel is closed) */}
          {!showChatPanel && (
            <div className="hidden lg:block w-72 lg:w-80 space-y-4 sm:space-y-6">
              {/* Explore More Topics */}
              <Card className="bg-[hsl(var(--card-bg))] border-[hsl(var(--card-border))]">
                <CardContent className="p-4 sm:p-6 space-y-3 sm:space-y-4">
                  <h3 className="text-lg sm:text-xl font-bold text-foreground flex items-center gap-2">
                    <Sparkles className="w-4 h-4 sm:w-5 sm:h-5 text-[hsl(var(--primary))]" />
                    Explore More Topics
                  </h3>

                  <div className="space-y-3">
                    {loadingTopics ? (
                      <div className="text-sm text-muted-foreground py-4 text-center">Loading topics...</div>
                    ) : exploreTopics.length === 0 ? (
                      <div className="flex flex-col items-center justify-center p-6 text-center rounded-xl bg-[hsl(var(--main-bg))] border border-[hsl(var(--card-border))] border-dashed my-2 group">
                        <Sparkles className="w-6 h-6 text-purple-400 mb-2 opacity-50 group-hover:opacity-100 group-hover:animate-pulse transition-opacity" />
                        <p className="text-sm text-muted-foreground font-medium">New topics arriving soon!</p>
                        <p className="text-xs text-muted-foreground/70 mt-1">Stay tuned for more mind-expanding content.</p>
                      </div>
                    ) : (
                      exploreTopics.slice(0, 4).map((topic) => (
                        <Card
                          key={topic.id}
                          className="bg-[hsl(var(--main-bg))] border-[hsl(var(--card-border))] overflow-hidden hover:border-[hsl(var(--primary))] transition-all cursor-pointer group"
                          onClick={() => handleTopicClick(topic)}
                        >
                          <CardContent className="p-3 sm:p-4">
                            <div className="flex items-center gap-2 sm:gap-3">
                              <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-lg bg-gradient-to-br ${topic.color} flex items-center justify-center text-lg sm:text-2xl`}>
                                {topic.icon}
                              </div>
                              <div className="flex-1">
                                <h4 className="font-bold text-xs sm:text-sm text-foreground group-hover:text-[hsl(var(--primary))] transition-colors">
                                  {topic.title}
                                </h4>
                                <p className="text-[10px] sm:text-xs text-muted-foreground">{topic.contentCount} resources</p>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))
                    )}
                  </div>

                  <Button
                    className="w-full bg-[hsl(var(--primary))] hover:bg-[hsl(var(--teal-glow))] text-white font-semibold"
                    onClick={handleViewAllTopics}
                  >
                    View All Topics
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </CardContent>
              </Card>

              {/* Related News - Compact Design */}
              <Card className="bg-[hsl(var(--card-bg))] border-[hsl(var(--card-border))]">
                <CardContent className="p-4 sm:p-6 space-y-3 sm:space-y-4">
                  <h3 className="text-lg sm:text-xl font-bold text-foreground flex items-center gap-2">
                    <Sparkles className="w-4 h-4 sm:w-5 sm:h-5 text-[hsl(var(--primary))]" />
                    Related News
                  </h3>

                  <div className="space-y-3">
                    {loadingNews ? (
                      <div className="text-sm text-muted-foreground py-4 text-center">Loading news...</div>
                    ) : relatedNews.length === 0 ? (
                      <div className="flex flex-col items-center justify-center p-6 text-center rounded-xl bg-[hsl(var(--main-bg))] border border-[hsl(var(--card-border))] border-dashed my-2 group">
                        <Sparkles className="w-6 h-6 text-blue-400 mb-2 opacity-50 group-hover:opacity-100 group-hover:animate-pulse transition-opacity" />
                        <p className="text-sm text-muted-foreground font-medium">Extra! Extra! Coming Soon...</p>
                        <p className="text-xs text-muted-foreground/70 mt-1">We're sourcing the latest news for you.</p>
                      </div>
                    ) : (
                      relatedNews.map((news) => (
                        <Card
                          key={news.id}
                          className="bg-[hsl(var(--main-bg))] border-[hsl(var(--card-border))] overflow-hidden hover:border-[hsl(var(--primary))] transition-all cursor-pointer group"
                          onClick={() => handleNewsClick(news)}
                        >
                          <CardContent className="p-2 sm:p-3">
                            {/* Horizontal compact layout */}
                            <div className="flex gap-2 sm:gap-3">
                              {/* Small thumbnail on left */}
                              <div className="relative w-16 h-16 sm:w-20 sm:h-20 flex-shrink-0 rounded-lg overflow-hidden">
                                <img
                                  src={news.image}
                                  alt={news.title}
                                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                />
                                <Badge className="absolute top-0.5 sm:top-1 left-0.5 sm:left-1 bg-[hsl(var(--primary))] text-white text-[8px] sm:text-[10px] px-1 sm:px-1.5 py-0.5">
                                  {news.category}
                                </Badge>
                              </div>

                              {/* Content on right */}
                              <div className="flex-1 min-w-0 space-y-0.5 sm:space-y-1">
                                <h4 className="font-bold text-[10px] sm:text-xs text-foreground group-hover:text-[hsl(var(--primary))] transition-colors line-clamp-2 leading-tight">
                                  {news.title}
                                </h4>
                                <p className="text-[8px] sm:text-[10px] text-muted-foreground line-clamp-2 leading-tight">
                                  {news.summary}
                                </p>
                                <div className="flex items-center justify-between text-[7px] sm:text-[9px] text-muted-foreground pt-0.5 sm:pt-1">
                                  <span className="truncate">{news.source}</span>
                                  <span className="flex-shrink-0 ml-1 sm:ml-2">{news.readTime}</span>
                                </div>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))
                    )}
                  </div>

                  <Button
                    className="w-full bg-[hsl(var(--primary))] hover:bg-[hsl(var(--teal-glow))] text-white font-semibold"
                    onClick={() => navigate('/student/curiosity/news')}
                  >
                    View All News
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </div>

      {/* Right Sidebar - Chat Panel (shown when chat panel is open) - Fixed Position */}
      {/* Expands to full height from top when open - Constrained to right sidebar width */}
      {showChatPanel && !isSmallScreen && (
        <div className="fixed right-0 top-0 h-screen w-[400px] z-50">
          <BudleeAIChatPanel
            onClose={() => {
              setShowChatPanel(false);
              setPrefilledText('');
            }}
            prefilledText={prefilledText}
            pageContext="curiosity"
            onClearPrefilledText={() => setPrefilledText('')}
            subjectId={selectedSubjectId || null}
            chapterId={selectedChapterId || null}
          />
        </div>
      )}

      {showChatPanel && isSmallScreen && (
        <div className="fixed inset-x-0 bottom-0 h-1/2 z-50 bg-[hsl(var(--main-bg))] border-t border-[hsl(var(--card-border))]">
          <BudleeAIChatPanel
            onClose={() => {
              setShowChatPanel(false);
              setPrefilledText('');
            }}
            prefilledText={prefilledText}
            pageContext="curiosity"
            onClearPrefilledText={() => setPrefilledText('')}
            subjectId={selectedSubjectId || null}
            chapterId={selectedChapterId || null}
            isMobileSplit={true}
          />
        </div>
      )}


    </div>
  );
}

